/**
 * Point d'entrée d'un lien d'invitation `/invite/:token` (Phase D, migration
 * 0259) — saisie du nom → session (réutilisée ou anonyme) → rejoint la salle
 * → salle d'attente si `lobby_enabled` → `RoomPage`. Un compte réel déjà
 * connecté sur CET appareil (session existante) est reconnu tel quel par
 * `join_visio_room_by_invite` (rôle serveur dérivé de `profiles.role`/
 * l'identité hôte) — mais `apps/tutor` n'a aucune UI de connexion propre :
 * le chemin normal pour un compte réel reste le handoff SSO (`/join/:roomId`),
 * ce lien est avant tout celui qu'on partage à un invité externe.
 */
import { useEffect, useState } from 'react';
import { AvatarCircle, Button, cn, useTurnstile } from '@jeraisonne/ui';
import { RoomPage } from '@jeraisonne/visio';
import { supabase, supabaseUrl } from '@/lib/supabase';
import { appUrl } from '@/lib/app-url';
import type { VisioRole } from '@jeraisonne/visio';
import {
  ensureGuestSession,
  fetchMyParticipant,
  joinRoomByInvite,
  watchMyParticipantStatus,
} from './join';
import { translateInviteError } from './errors';
import { DEFAULT_GUEST_AVATAR, GUEST_AVATAR_PRESETS, guestAvatarUrl } from './guest-avatars';

type Screen =
  | { kind: 'name_entry' }
  | { kind: 'joining' }
  | { kind: 'waiting'; roomId: string; userId: string; displayName: string }
  | { kind: 'denied' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; roomId: string; me: { userId: string; role: VisioRole; displayName: string } };

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-4 py-6 text-center sm:py-8">
      {children}
    </div>
  );
}

export function InvitePage({ token }: { token: string }) {
  const [screen, setScreen] = useState<Screen>({ kind: 'name_entry' });
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState<string>(DEFAULT_GUEST_AVATAR);
  // Jeton CAPTCHA pour signInAnonymously (cf. useTurnstile : indisponible → sans jeton).
  const turnstile = useTurnstile();

  async function handleJoin() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setScreen({ kind: 'joining' });

    const session = await ensureGuestSession(trimmed, turnstile.getToken);
    if ('error' in session) {
      setScreen({ kind: 'error', message: translateInviteError(session.error) });
      return;
    }

    const result = await joinRoomByInvite(token, trimmed, avatar);
    if (result.status === 'error') {
      setScreen({ kind: 'error', message: translateInviteError(result.message) });
      return;
    }

    if (result.waiting) {
      setScreen({
        kind: 'waiting',
        roomId: result.roomId,
        userId: session.userId,
        displayName: trimmed,
      });
      return;
    }

    const me = await fetchMyParticipant(result.roomId, session.userId);
    setScreen({
      kind: 'ready',
      roomId: result.roomId,
      me: {
        userId: session.userId,
        role: me?.role ?? 'guest',
        displayName: me?.displayName ?? trimmed,
      },
    });
  }

  // Salle d'attente : suit SA PROPRE ligne jusqu'à admission/refus.
  useEffect(() => {
    if (screen.kind !== 'waiting') return;
    const { roomId, userId, displayName } = screen;
    const stop = watchMyParticipantStatus(roomId, userId, (status) => {
      if (status === 'admitted') {
        void fetchMyParticipant(roomId, userId).then((me) => {
          setScreen({
            kind: 'ready',
            roomId,
            me: {
              userId,
              role: me?.role ?? 'guest',
              displayName: me?.displayName ?? displayName,
            },
          });
        });
      } else if (status === 'denied' || status === 'removed') {
        setScreen({ kind: 'denied' });
      }
    });
    return stop;
  }, [screen]);

  if (screen.kind === 'name_entry' || screen.kind === 'joining') {
    return (
      <CenteredMessage>
        <p className="text-ink-900 text-lg font-semibold">Rejoindre la réunion</p>
        <div className="w-full max-w-xs space-y-3 text-left">
          <div>
            <label
              htmlFor="invite-name"
              className="text-ink-700 mb-1.5 block text-xs font-semibold"
            >
              Ton prénom
            </label>
            <input
              id="invite-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              autoFocus
              placeholder="Camille"
              disabled={screen.kind === 'joining'}
              className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 w-full rounded-xl border px-3.5 text-sm focus:outline-none disabled:opacity-50"
            />
          </div>
          <fieldset disabled={screen.kind === 'joining'}>
            <legend className="text-ink-700 mb-1.5 block text-xs font-semibold">Ton avatar</legend>
            <div className="grid grid-cols-4 gap-2">
              {GUEST_AVATAR_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  aria-label={preset.label}
                  aria-pressed={avatar === preset.key}
                  onClick={() => setAvatar(preset.key)}
                  className={cn(
                    'flex min-h-[2.75rem] items-center justify-center rounded-xl border-2 p-1 active:scale-[0.98] disabled:opacity-50',
                    avatar === preset.key ? 'border-primary-500' : 'border-transparent',
                  )}
                >
                  <AvatarCircle name={preset.label} src={guestAvatarUrl(preset.key)} size="md" />
                </button>
              ))}
            </div>
          </fieldset>
          <Button
            onClick={() => void handleJoin()}
            disabled={!name.trim() || screen.kind === 'joining'}
            className="min-h-[2.75rem] w-full active:scale-[0.98]"
          >
            {screen.kind === 'joining' ? 'Connexion…' : 'Rejoindre'}
          </Button>
          <div ref={turnstile.containerRef} className="flex justify-center empty:hidden" />
        </div>
      </CenteredMessage>
    );
  }

  if (screen.kind === 'waiting') {
    return (
      <CenteredMessage>
        <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        <p className="text-ink-900 text-lg font-semibold">Salle d'attente</p>
        <p className="text-ink-500 max-w-sm text-sm">
          En attente que l'organisateur t'admette dans la réunion…
        </p>
      </CenteredMessage>
    );
  }

  if (screen.kind === 'denied') {
    return (
      <CenteredMessage>
        <p className="text-ink-900 text-lg font-semibold">Entrée refusée</p>
        <p className="text-ink-500 max-w-sm text-sm">
          L'organisateur n'a pas admis ta demande, ou t'a retiré de la réunion.
        </p>
        <Button asChild className="min-h-[2.75rem] w-full max-w-xs">
          <a href={appUrl()}>Retour à JeRaisonne</a>
        </Button>
      </CenteredMessage>
    );
  }

  if (screen.kind === 'error') {
    return (
      <CenteredMessage>
        <p className="text-ink-900 text-lg font-semibold">Impossible de rejoindre</p>
        <p className="text-ink-500 max-w-sm text-sm">{screen.message}</p>
        <Button asChild className="min-h-[2.75rem] w-full max-w-xs">
          <a href={appUrl()}>Retour à JeRaisonne</a>
        </Button>
      </CenteredMessage>
    );
  }

  return (
    <RoomPage
      roomId={screen.roomId}
      me={screen.me}
      db={supabase}
      supabaseUrl={supabaseUrl}
      onExit={() => {
        window.location.href = appUrl();
      }}
    />
  );
}
